/**
 * Turns an auth failure into a message in the user's language.
 *
 * The auth layer (`src/Auth/utils/error.utils.ts`) reports failures as `{ status, message }` with
 * an English fallback message and **no error code**, so mapping has to key on the HTTP status.
 * Without this, a PT-BR user sees "Invalid credentials. Please check your email and password."
 *
 * Not a hook despite the old name: it holds no state and calls nothing from React, so the `use`
 * prefix only made React's rules-of-hooks reject it wherever it was called.
 *
 * @module Pages/Onboard/Login
 */

import type { CustomLocale } from '../Types/Login.locale.types';

/** Status reported by `handleFetchError` when the request never reached the server. */
const NETWORK_FAILURE = 0;

/**
 * Resolves an auth failure into a localized message.
 *
 * @param error - Whatever the auth layer threw.
 * @param customLocale - Translator for the login namespace.
 */
export function resolveLoginError(error: unknown, customLocale: CustomLocale): string {
  const status =
    error && typeof error === 'object' && 'status' in error
      ? (error as { status?: number }).status
      : undefined;

  switch (status) {
    case NETWORK_FAILURE:
      return customLocale(
        'errors.networkError',
        'Erro de rede. Verifique sua conexão e tente novamente.',
      );
    case 401:
      return customLocale('errors.invalidCredentials', 'E-mail ou senha inválidos.');
    case 403:
      return customLocale(
        'errors.accountDisabled',
        'Sua conta foi desabilitada. Entre em contato com o suporte.',
      );
    // The BFF locks an account for 15 minutes after 5 failed attempts.
    case 423:
    case 429:
      return customLocale(
        'errors.accountLocked',
        'Sua conta foi bloqueada por excesso de tentativas. Tente de novo em alguns minutos.',
      );
    default:
      return customLocale('errors.invalidCredentials', 'E-mail ou senha inválidos.');
  }
}
